import { AddOutlined } from '@mui/icons-material';
import { Button, Link, List, ListItem, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { DateInput } from 'src/basic-components/date/date-input';
import { InitFailed } from 'src/basic-components/init-failed';
import { Loading } from 'src/basic-components/loading';
import { N } from 'src/basic-components/number';
import { Rows } from 'src/basic-components/table/rows';
import T from 'src/basic-components/text';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import { useApi } from 'src/contexts/api-provider';
import { userFundActivityStatuses } from 'src/enums';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { ManageTodoModal } from './manage-todo-modal';
import { Form, Formik } from 'formik';
import { FormikTextView } from 'src/basic-components/formik-inputs';
import { FormikSelectView } from 'src/basic-components/formik-inputs/formik-select-view';
import { Years } from 'src/utils/enums';
import { persianMonths } from 'src/utils/persian-calendar';
import useResponsive from 'src/hooks/useResponsive';
import { LoadingButton } from '@mui/lab';

export default function TodosApp() {
  const theme = useTheme();
  const formikRef = useRef(null);
  const isDesktop = useResponsive('up', 'lg');
  const [isLoading, setIsLoading] = useState(false);
  const [model, setModel] = useState();
  const [selectedRow, setSelectedRow] = useState();
  const { CoreApi } = useApi();
  const apiName = 'adminTodoApi';
  const { t } = useTranslation();
  const api = {
    init: CoreApi(apiName, 'initTodos'),
  };

  useEffect(() => {
    if (model) return;
    filterData({});
  }, [model]);

  const filterData = (values) => {
    setIsLoading(true);
    api
      .init(values)
      .then(setModel)
      .catch(setModel)
      .finally(() => setIsLoading(false));
  };

  const columns = [
    {
      accessorKey: 'text',
      header: <T>todo-text</T>,
    },
    {
      accessorKey: 'statusId',
      header: <T>status</T>,
    },
    {
      accessorKey: 'dueYear',
      header: <T>due-date</T>,
      cell: ({ row }) => (
        <div>
          {row.original.dueYear} / {row.original.dueMonth}
        </div>
      ),
    },
    {
      accessorKey: 'id',
      header: '',
      cell: ({ row }) => (
        <Button variant="outlined" onClick={() => setSelectedRow(row.original)}>
          <T>manage</T>
        </Button>
      ),
    },
  ];

  if (!model) return <Loading />;
  if (model instanceof Error) return <InitFailed ex={model} onRetry={() => setModel(null)} />;

  const onSave = (todo) => {
    setSelectedRow(null);
    filterData(formikRef.current?.values);
  };

  return (
    <Page
      backUrl={PATH_DASHBOARD.root}
      breadcrumbs={
        <HeaderBreadcrumbs links={[{ name: 'dashboard', href: PATH_DASHBOARD.root }, { name: 'manage-todos' }]} />
      }
      Control={
        <Button
          size="small"
          variant="outlined"
          onClick={() => setSelectedRow({ dueMonth: model?.month, dueYear: model?.year })}
          endIcon={<AddOutlined />}
        >
          <T>add</T>
        </Button>
      }
    >
      <Formik
        innerRef={formikRef}
        initialValues={{ dueMonth: model?.month, dueYear: model?.year }}
        onSubmit={filterData}
      >
        {(formik) => (
          <Form>
            <Stack flex flexDirection="row" gap={2} flexWrap="wrap" sx={{ mt: 1 }}>
              <FormikSelectView
                formik={formik}
                sx={{ width: !isDesktop ? '100%' : '11rem' }}
                name="dueYear"
                label="due-year"
                items={[{ id: 0, title: '' }, ...Years]}
              />
              <FormikSelectView
                formik={formik}
                sx={{ width: !isDesktop ? '100%' : '11rem' }}
                name="dueMonth"
                label="due-month"
                items={[{ id: 0, title: '' }, ...persianMonths]}
              />
              <FormikSelectView
                sx={{ width: !isDesktop ? '100%' : '11rem' }}
                formik={formik}
                name="statusId"
                label="status"
                items={[
                  {
                    id: 'open',
                    title: t('open'),
                  },
                  {
                    id: 'pending',
                    title: t('pending'),
                  },
                  {
                    id: 'completed',
                    title: t('completed'),
                  },
                ]}
              />
              <LoadingButton
                sx={{ width: !isDesktop ? '100%' : '80px' }}
                loading={isLoading}
                type="submit"
                variant="contained"
                size="small"
              >
                <T>filter</T>
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>

      {isLoading ? (
        <Loading />
      ) : (
        <List>
          {model.todos.map((todo) => (
            <ListItem
              button
              onClick={() => setSelectedRow(todo)}
              sx={{
                padding: 1,
                mb: 1,
                width: '100%',
                border: '1px dashed ' + theme.palette.grey[300],
                borderRadius: 0.5,
                borderLeftStyle: 'solid',
                borderLeftWidth: 5,
                borderLeftColor: todo.statusId == 'open' ? 'red' : todo.statusId == 'pending' ? 'orange' : 'green',
                textDecoration: todo.statusId == 'completed' ? 'line-through' : undefined,

                backgroundColor:
                  todo.statusId == 'open' ? '#ff00001f' : todo.statusId == 'pending' ? '#ffa5001f' : '#0080001f',
                fontStyle: todo.statusId == 'pending' ? 'italic' : undefined,
              }}
            >
              <Stack flex flexDirection="row">
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: todo.statusId == 'open' ? 'bold' : undefined,
                    fontStyle: todo.statusId == 'pending' ? 'italic' : undefined,
                  }}
                >
                  {todo.text}
                </Typography>
              </Stack>
            </ListItem>
          ))}
        </List>
      )}
      <ManageTodoModal
        open={selectedRow != null}
        data={selectedRow}
        onSave={onSave}
        onClose={() => setSelectedRow(null)}
      />
    </Page>
  );
}
